/**
 * Checklist "Conteúdo do laudo" (E36.F4) — substitui o WorkspaceStepper // ux-neutrality:allow doc-comment
 * decorativo na coluna esquerda do workspace. // ux-neutrality:allow doc-comment
 *
 * Lista o que entra na seção da contestada no laudo: a imagem da contestada // ux-neutrality:allow doc-comment
 * (sempre incluída — checkbox desabilitado) + as capturas da mesa, cada uma // ux-neutrality:allow doc-comment
 * com thumbnail, rótulo editável (rename no blur/Enter), checkbox de inclusão // ux-neutrality:allow doc-comment
 * e remover. Estado vem do hook na página (compartilhado com o botão // ux-neutrality:allow doc-comment
 * "Capturar para o laudo" da mesa). // ux-neutrality:allow doc-comment
 */
import { useEffect, useState } from 'react';
import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { fetchLaudoAttachmentBlob, type LaudoAttachmentData } from '@/lib/laudo_attachments';

export interface LaudoContentChecklistProps {
  caseId: string;
  analysisId: string;
  attachments: LaudoAttachmentData[];
  saveError: boolean;
  onRename: (id: string, label: string) => Promise<void>;
  onSetIncluded: (id: string, includeInLaudo: boolean) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}

/** Thumbnail autenticada da captura (mesma técnica do useSignatureBlob). */
function useAttachmentThumb(
  caseId: string,
  analysisId: string,
  attachmentId: string,
): string | null {
  const client = useApiClient();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    fetchLaudoAttachmentBlob(client, caseId, analysisId, attachmentId)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [client, caseId, analysisId, attachmentId]);

  return url;
}

interface AttachmentItemProps {
  caseId: string;
  analysisId: string;
  attachment: LaudoAttachmentData;
  onRename: (id: string, label: string) => Promise<void>;
  onSetIncluded: (id: string, includeInLaudo: boolean) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}

function AttachmentItem({
  caseId,
  analysisId,
  attachment,
  onRename,
  onSetIncluded,
  onRemove,
}: AttachmentItemProps): JSX.Element {
  const thumb = useAttachmentThumb(caseId, analysisId, attachment.id);

  function commitRename(value: string): void {
    const label = value.trim();
    if (label === '' || label === attachment.label) return;
    void onRename(attachment.id, label);
  }

  return (
    <li className="laudo-content-item" data-testid={`laudo-content-item-${attachment.id}`}>
      <input
        type="checkbox"
        className="accent-accent-700"
        checked={attachment.include_in_laudo}
        onChange={(e) => void onSetIncluded(attachment.id, e.target.checked)}
        aria-label={`${ptBR.workspaceCaso.laudoContentInclude}: ${attachment.label}`}
        data-testid={`laudo-content-include-${attachment.id}`}
      />
      {thumb !== null ? (
        <img className="laudo-content-thumb" src={thumb} alt={attachment.label} />
      ) : (
        <span className="laudo-content-thumb laudo-content-thumb--empty" aria-hidden="true" />
      )}
      <input
        key={attachment.label}
        type="text"
        className="laudo-content-label"
        defaultValue={attachment.label}
        aria-label={ptBR.workspaceCaso.laudoContentRenameLabel}
        maxLength={200}
        onBlur={(e) => commitRename(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        data-testid={`laudo-content-label-${attachment.id}`}
      />
      <button
        type="button"
        className="laudo-content-remove"
        onClick={() => void onRemove(attachment.id)}
        aria-label={`${ptBR.workspaceCaso.laudoContentRemove}: ${attachment.label}`}
        data-testid={`laudo-content-remove-${attachment.id}`}
      >
        ×
      </button>
    </li>
  );
}

export function LaudoContentChecklist({
  caseId,
  analysisId,
  attachments,
  saveError,
  onRename,
  onSetIncluded,
  onRemove,
}: LaudoContentChecklistProps): JSX.Element {
  return (
    <Blueprint as="section" className="laudo-content" data-testid="laudo-content-checklist">
      <CardKicker>{ptBR.workspaceCaso.laudoContentTitle}</CardKicker>
      <ul className="laudo-content-list">
        {/* A imagem da contestada sempre abre a seção do laudo — não é opcional. */}
        <li className="laudo-content-item" data-testid="laudo-content-contested">
          <input
            type="checkbox"
            className="accent-accent-700"
            checked
            disabled
            aria-label={`${ptBR.workspaceCaso.laudoContentInclude}: ${ptBR.workspaceCaso.contestedTitle}`}
          />
          <span className="laudo-content-fixed-label">
            {ptBR.workspaceCaso.contestedTitle}
            <MonoData variant="label" className="laudo-content-always">
              {ptBR.workspaceCaso.laudoContentAlways}
            </MonoData>
          </span>
        </li>
        {attachments.map((attachment) => (
          <AttachmentItem
            key={attachment.id}
            caseId={caseId}
            analysisId={analysisId}
            attachment={attachment}
            onRename={onRename}
            onSetIncluded={onSetIncluded}
            onRemove={onRemove}
          />
        ))}
      </ul>
      {attachments.length === 0 && (
        <p className="laudo-content-empty" data-testid="laudo-content-empty">
          {ptBR.workspaceCaso.laudoContentEmpty}
        </p>
      )}
      {saveError && (
        <p className="laudo-content-error" role="alert" data-testid="laudo-content-error">
          {ptBR.workspaceCaso.laudoContentSaveError}
        </p>
      )}
    </Blueprint>
  );
}
